import { getProfile } from "../../../../core/service/users.service";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setUserProfile } from "../../../../core/redux/action/userProfile";
import moment from "moment";
import excelJs from "exceljs";
// material
import {
  Button,
  Card,
  Container,
  Table,
  Stack,
  Link,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
} from "@mui/material";

// components
import SearchNotFound from "../../../SearchNotFound";
import UserListToolbar from "./UserListToolbar";
import UserListHead from "./UserListHead";
import Page from "../../../Page";
import { config } from "../../../../core/service/config.service";
// ----------------------------------------------------------------------

const ROLE_ADMIN = "admin";
const ROLE_MENTOR = "mentor";
const ROLE_PHD3Y = "phd_3y";
const ROLE_PHD5Y = "phd_5y";
const ROLE_POSTDOC = "post_doc";

const TABLE_HEAD = [
  { id: "name", label: "Name", alignRight: false },
  { id: "faculty", label: "Faculty", alignRight: false },
  { id: "program", label: "Program", alignRight: false },
  { id: "myMentor", label: "Mentor", alignRight: false },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return array.filter((e) => {
      const name = `${e.title_name_en} ${e.name_en} ${e.middle_en || ""} ${
        e.surname_en
      }`;
      return name.toLowerCase().indexOf(query.toLowerCase()) !== -1;
    });
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function StdUsersTable({ ListUsers }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - ListUsers.length) : 0;

  const filteredUsers = applySortFilter(
    ListUsers,
    getComparator(order, orderBy),
    filterName
  );

  const isUserNotFound = filteredUsers.length === 0;

  const exportExcel = async () => {
    const workbook = new excelJs.Workbook();
    const ws = workbook.addWorksheet(`Report`);
    ws.columns = [
      {
        header: "Name",
        key: "name",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Faculty",
        key: "faculty",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Program",
        key: "program",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Mentor",
        key: "myMentor",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
    ];

    ws.getRow(1).height = 30;

    await Promise.all(
      filteredUsers.map(async (val, index) => {
        ws.addRow([
          `${val.name_en} ${val.middle_en || ""} ${val.surname_en}`,
          val.faculty,
          val.role_nm === ROLE_POSTDOC
            ? "Post Doc"
            : val.role_nm === ROLE_PHD3Y
            ? "PhD 3 year"
            : "PhD 5 year",
          `${val.mentor_title_en} ${val.mentor_name_en} ${
            val.mentor_middle_en || ""
          } ${val.mentor_surname_en}`,
        ]);
        ws.getRow(index + 2).font = {
          ...ws.getRow(index + 2).font,
          bold: false,
        };
      })
    );

    workbook.xlsx
      .writeBuffer({
        base64: true,
      })
      .then((xls64) => {
        // build anchor tag and attach file (works in chrome)
        var a = document.createElement("a");
        var data = new Blob([xls64], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        var url = URL.createObjectURL(data);
        a.href = url;
        a.download = `GRANTEE_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 0);
      })
      .catch(function (error) {
        console.log(error.message);
      });
  };

  return (
    <Page title="StdMember">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Grantee
          </Typography>
          <Link href={`${config.host_api}export/grantee`}>
            <Button variant="contained">Export excel</Button>
          </Link>
        </Stack>

        <Card>
          <UserListToolbar
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={ListUsers.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, i) => {
                    const {
                      faculty,
                      middle_en,
                      name_en,
                      role_nm,
                      surname_en,
                      mentor_middle_en,
                      mentor_name_en,
                      mentor_surname_en,
                      mentor_title_en,
                    } = row;

                    return (
                      <TableRow key={i} hover tabIndex={-1}>
                        <TableCell></TableCell>
                        <TableCell component="th" scope="row" padding="none">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Link
                              underline="hover"
                              color="inherit"
                              variant="subtitle2"
                              component="button"
                              onClick={async () => {
                                const data = await getProfile(row?.user_id);
                                await dispatch(setUserProfile(data));
                                const { role, personal_infomation } = data;
                                navigate(
                                  `/anotherProfile/${role.role}/${personal_infomation.student_id}`,
                                  {
                                    replace: true,
                                  }
                                );
                              }}
                            >
                              {`${name_en} ${middle_en || ""} ${surname_en}`}
                            </Link>
                          </Stack>
                        </TableCell>
                        <TableCell align="left">{faculty}</TableCell>
                        <TableCell align="left">
                          {role_nm === ROLE_POSTDOC
                            ? "Post Doc"
                            : role_nm === ROLE_PHD3Y
                            ? "PhD 3 year"
                            : "PhD 5 year"}
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="subtitle2">{`${mentor_title_en} ${mentor_name_en} ${
                            mentor_middle_en || ""
                          } ${mentor_surname_en}`}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={ListUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
